import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Download, Trash2, StickyNote } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Negotiation", "Won", "Lost", "Archived"];
const STATUS_COLOR = {
  New: "bg-primary/15 text-primary", Contacted: "bg-accent/15 text-accent", Qualified: "bg-chart-4/15 text-chart-4",
  "Proposal Sent": "bg-chart-5/15 text-chart-5", Negotiation: "bg-chart-5/15 text-chart-5",
  Won: "bg-success/15 text-success", Lost: "bg-destructive/15 text-destructive", Archived: "bg-muted text-muted-foreground",
};

export default function AdminLeads() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const params = { page, limit: 15 };
  if (status !== "all") params.status = status;
  if (type !== "all") params.type = type;
  if (search) params.search = search;

  const { data } = useQuery({
    queryKey: ["admin-leads", status, type, search, page],
    queryFn: async () => (await api.get("/admin/leads", { params })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-leads"] });
    qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
  };

  const updateStatus = async (id, newStatus) => {
    await api.put(`/admin/leads/${id}`, { status: newStatus });
    toast.success("Lead updated");
    refresh();
    if (active?.id === id) setActive({ ...active, status: newStatus });
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data: res } = await api.post(`/admin/leads/${active.id}/notes`, { body: note });
    setActive({ ...active, notes: [...(active.notes || []), res.note] });
    setNote("");
    toast.success("Note added");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Lead deleted"); setActive(null); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => { window.open(`${API}/admin/leads/export`, "_blank"); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Leads / CRM</h1><p className="text-sm text-muted-foreground">{data?.total ?? 0} total leads</p></div>
        <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="leads-export"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, email, company..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid="leads-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="leads-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{["contact", "quote", "consultation", "enquiry"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-5 overflow-hidden rounded-xl border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">Name</th><th className="p-3">Type</th><th className="p-3 hidden md:table-cell">Service</th><th className="p-3">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {(data?.items || []).map((l) => (
                <tr key={l.id} className="border-t border-border hover:bg-secondary/30 cursor-pointer" onClick={() => setActive(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="p-3"><div className="font-semibold">{l.name}</div><div className="text-xs text-muted-foreground">{l.email}</div></td>
                  <td className="p-3"><Badge variant="outline">{l.type}</Badge></td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground">{l.service || "—"}</td>
                  <td className="p-3"><Badge className={STATUS_COLOR[l.status]}>{l.status}</Badge></td>
                  <td className="p-3 text-right text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {(data?.items || []).length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No leads found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {data?.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {active && (
            <>
              <SheetHeader><SheetTitle>{active.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <Info label="Email" value={active.email} /><Info label="Phone" value={active.phone || "—"} />
                  <Info label="Company" value={active.company || "—"} /><Info label="Type" value={active.type} />
                  <Info label="Service" value={active.service || "—"} /><Info label="Budget" value={active.budget || "—"} />
                  <Info label="Source" value={active.source || "—"} /><Info label="Consent" value={active.consent ? "Yes" : "No"} />
                </div>
                {active.message && <div><p className="text-xs text-muted-foreground">Message</p><p className="mt-1 rounded-lg border border-border p-3">{active.message}</p></div>}
                {active.utm && Object.keys(active.utm).length > 0 && <div><p className="text-xs text-muted-foreground">UTM</p><pre className="mt-1 overflow-x-auto rounded-lg border border-border p-3 text-xs">{JSON.stringify(active.utm, null, 2)}</pre></div>}

                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Select value={active.status} onValueChange={(v) => updateStatus(active.id, v)}>
                    <SelectTrigger className="mt-1.5" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><StickyNote className="h-3.5 w-3.5" /> Notes</p>
                  <div className="mt-2 space-y-2">
                    {(active.notes || []).map((n, i) => <div key={i} className="rounded-lg border border-border p-2.5 text-xs"><p>{n.body}</p><p className="mt-1 text-muted-foreground">{n.author} · {new Date(n.created_at).toLocaleString()}</p></div>)}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." data-testid="lead-note-input" />
                    <Button onClick={addNote} data-testid="lead-note-add">Add</Button>
                  </div>
                </div>

                <Button variant="destructive" className="w-full" onClick={() => del(active.id)} data-testid="lead-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete lead</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Info({ label, value }) {
  return <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium break-words">{value}</p></div>;
}
